import type * as Bible from "./bible";

/* c8 ignore next */
export const CACHE_VERSION = 1;

export const CACHE_NAMES = {
  api: `api-cache-v${CACHE_VERSION}`,
};

export const CACHE_DELAY = 3000;

export class APIClient {
  static shared = new APIClient();

  #baseUrl: string;

  get baseUrl() {
    return this.#baseUrl;
  }

  get headers(): Record<string, string> {
    return {
      "api-auth": "4JEoXCgInXe2Y4AIMcF3Tg2C4oO5vq8m",
    };
  }

  constructor(baseUrl: string = __API_BASE_URL__) {
    this.#baseUrl = baseUrl;
    if (__API_CACHE_ENABLED__) {
      APIClient.prepareCaches();
    }
  }

  protected async cache(request: Request, response: Response) {
    if (__API_CACHE_ENABLED__) {
      try {
        const cache = await caches.open(CACHE_NAMES.api);
        return await cache.put(request, response);
        /* c8 ignore next */
      } catch {}
      /* c8 ignore next */
    }
  }

  protected async cached(request: Request) {
    if (__API_CACHE_ENABLED__) {
      try {
        const cache = await caches.open(CACHE_NAMES.api);
        return await cache.match(request);
        /* c8 ignore next */
      } catch {}
      /* c8 ignore next */
    }
    /* c8 ignore next */
    return undefined;
  }

  protected async fetch<T>(
    route: Route,
    controller?: AbortController,
  ): Promise<T> {
    const request = await this.request(route, controller);
    const response = await this.response(request, controller);
    if (response.status < 200 || response.status > 299) {
      try {
        const json = await response.json();
        if (typeof json.error === "string") {
          throw new Error(json.error);
        }
        throw new Error(`Error (${response.status})`);
      } catch (error) {
        throw error;
      }
    }
    const responseClone = response.clone();
    const responseJSON = await response.json();
    await this.cache(request, responseClone);
    return responseJSON;
  }

  async request(route: Route, controller?: AbortController) {
    return new Request(`${this.baseUrl}${route.path}`, {
      headers: {
        Accept: "application/json; charset=utf-8",
        ["Content-Type"]: "application/json; charset=utf-8",
        ...this.headers,
        ...route.headers,
      },
      mode: "cors",
      signal: controller?.signal,
    });
  }

  protected async response(request: Request, controller?: AbortController) {
    const responseCached = await this.cached(request);
    if (responseCached && !navigator.onLine) {
      return responseCached;
    }
    const responsePromise = fetch(request);
    let timeout: NodeJS.Timeout;
    const timeoutPromise = new Promise<Response>((resolve, reject) => {
      timeout = setTimeout(() => {
        if (responseCached) {
          resolve(responseCached);
          controller?.abort();
        } else {
          reject(new Error("CACHE_MISS"));
        }
      }, CACHE_DELAY);
    }).catch(() => responsePromise);
    const promises: Array<Promise<Response>> = [
      responsePromise,
      timeoutPromise,
    ];
    return await Promise.race(promises).finally(() => {
      clearTimeout(timeout);
    });
  }

  bibleChapter(id: number) {
    return this.fetch<Bible.ChapterPayload>(new RouteBibleChapter({ id }));
  }

  bibleQuery(query: string, translation: number, controller?: AbortController) {
    return this.fetch<Bible.QueryPayload>(
      new RouteBibleQuery({ query, translation }),
      controller,
    );
  }

  bibleTranslations() {
    return this.fetch<Bible.TranslationPayload[]>(new RouteBibleTranslations());
  }

  setBaseUrl(url: string) {
    this.#baseUrl = url;
  }

  static async prepareCaches() {
    try {
      const expectedCacheNames = new Set(Object.values(CACHE_NAMES));
      await caches.keys().then((cacheNames) =>
        Promise.all(
          cacheNames.map((cacheName) => {
            if (expectedCacheNames.has(cacheName)) {
              return Promise.resolve(false);
            }
            return caches.delete(cacheName);
          }),
        ),
      );
    } catch {}
  }
}

/////////////////////////////////////////////////////
// Routes
//////////////////////////////////////////////////

/* c8 ignore start */
export abstract class Route {
  get fixture(): string {
    throw new Error("INVARIANT");
  }

  get headers(): { [key: string]: string } {
    return {};
  }

  get path(): string {
    throw new Error("INVARIANT");
  }
}
/* c8 ignore stop */

export class RouteBibleChapter extends Route {
  get fixture() {
    return `api_bible_structure_chapter_${this.input.id}.json`;
  }

  get path() {
    return `/bible/structure/chapter/${this.input.id}/`;
  }

  constructor(private input: { id: number }) {
    super();
    this.input = input;
  }
}

export class RouteBibleQuery extends Route {
  get fixture() {
    const { query, translation } = this.input;
    const queryReplaced = query.toLowerCase().replace(/[^a-z0-9]/g, "_");
    return `api_bible_query_${translation}_${queryReplaced}.json`;
  }

  get path() {
    const { query, translation } = this.input;
    return `/bible/query/${translation}/${query}/`;
  }

  constructor(private input: { query: string; translation: number }) {
    super();
    this.input = input;
  }
}

export class RouteBibleTranslations extends Route {
  get fixture() {
    return `api_bible_translations.json`;
  }

  get path() {
    return `/bible/translations/`;
  }

  constructor() {
    super();
  }
}

/////////////////////////////////////////////////////
// Fixtures
//////////////////////////////////////////////////

/* c8 ignore start */
export class APIClientFixture extends APIClient {
  get baseUrl() {
    return "/__fixtures__/";
  }

  async request(route: Route) {
    return super.request(new RouteFixture(route));
  }
}

class RouteFixture extends Route {
  get fixture() {
    return this.route.fixture;
  }

  get headers() {
    return {
      ...super.headers,
      "x-fixture": this.fixture,
    };
  }

  get path() {
    return this.route.fixture;
  }

  constructor(private route: Route) {
    super();
    this.route = route;
  }
}

if (__TEST_INTEGRATION__) {
  APIClient.shared = new APIClientFixture();
}
/* c8 ignore stop */
